import { useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { attachmentsApi, type SubmittalFileKind } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';
import { uploadToJobFolder, buildAttachmentName, ATTACHMENT_NAME_HINT, GraphConsentError } from '@/lib/graphUpload';
import Button from './Button';

/**
 * Shared "Job Folder" card for RFI + Submittal detail. Uploads files to the
 * project's SharePoint Job Folder using the SIGNED-IN user's delegated Graph
 * token, then records only the link + metadata via attachmentsApi.
 *
 *  - Not linked (no jobFolderUrl): a dotted, muted "not linked" state — no
 *    upload control, with a pointer to link the folder in Projects → edit.
 *  - Linked: a solid card with an upload control + the attachment list.
 *
 * Admin consent for the Graph scope may still be pending; in that case the
 * upload fails with a friendly GraphConsentError message and never crashes.
 */

// Delegated scope, acquired ON DEMAND (NOT part of loginRequest).
const GRAPH_SCOPES = ['Files.ReadWrite.All'];

export interface FileAttachmentsProps {
  /**
   * Whether this user may attach/remove files.
   *
   * ⚠️ For rfi/submittal this must be projects.edit, NOT the controller's
   * projects.edit-OR-daily_logs.edit. attachments.service.ts assertScopedEntityAccess
   * restricts scoped editors (daily_logs.edit only) to entityType 'daily-log', so a
   * field leader passes the route guard and is then refused in the service.
   *
   * It matters more here than elsewhere: the SharePoint upload happens client-side
   * BEFORE the tracker call, so a rejected user has already written the file into
   * the Job Folder when the 403 lands — an orphaned file, not just a failed click.
   */
  canEdit: boolean;
  entityType: 'rfi' | 'submittal';
  entityId: number;
  jobFolderUrl: string | null;
  subfolder: 'RFIs' | 'Submittals';
  jobNumber: string;
  docNumber: string | null;
  shortDescSource: string;
  status: string;
  /**
   * Submittals only: list and upload for ONE side of one review round. The list shows
   * just the files recorded on that round with that kind, and an upload is recorded
   * against them. The caller builds `docNumber` and `status` for the round (the
   * revision goes in the file name), so this component stays ignorant of rounds.
   */
  round?: { id: number; kind: SubmittalFileKind };
  /** Label for the upload button. Default "Upload to Job Folder". */
  uploadLabel?: string;
  /**
   * Render only the list and the upload control, with no card, heading, or the
   * explanatory notes, for use inside another card that says those things once. A job
   * with no Job Folder renders nothing at all (the host card says so).
   */
  bare?: boolean;
}

const css = `
  .ew-fa-card { /* renders inside the tab's pw-card slot */ }
  .ew-fa-body { padding: var(--ew-s-4); display: grid; gap: var(--ew-s-3); }
  .ew-fa-bare { padding: 0; }

  /* Not-linked dotted state */
  .ew-fa-unlinked {
    border: 1px dashed var(--ew-n-300); border-radius: var(--ew-r-md);
    padding: var(--ew-s-4); display: grid; gap: var(--ew-s-2);
    background: var(--ew-n-50);
  }
  .ew-fa-unlinked-title {
    font: var(--ew-fw-semibold) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-600);
    display: flex; align-items: center; gap: 8px;
  }
  .ew-fa-folder-icon { font-size: 18px; line-height: 1; }

  .ew-fa-help { font: var(--ew-fw-regular) 12px/1.4 var(--ew-font); color: var(--ew-n-500); }
  .ew-fa-error {
    padding: 8px 12px; background: #FBE4DF; color: #8F2414; border-radius: var(--ew-r-md);
    font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.4 var(--ew-font);
  }
  .ew-fa-mono {
    font-family: var(--ew-font-mono, ui-monospace, monospace);
    background: var(--ew-n-100); border-radius: var(--ew-r-sm);
    padding: 2px 6px; color: var(--ew-n-700); word-break: break-all;
  }
  .ew-fa-label {
    font: var(--ew-fw-medium) 11px/1 var(--ew-font); letter-spacing: var(--ew-ls-caption);
    text-transform: uppercase; color: var(--ew-n-500);
  }

  /* Attachment list */
  .ew-fa-list { list-style: none; margin: 0; padding: 0; display: grid; gap: var(--ew-s-2); }
  .ew-fa-item {
    display: flex; align-items: flex-start; justify-content: space-between; gap: var(--ew-s-2);
    padding: 8px 10px; border: 1px solid var(--ew-n-100); border-radius: var(--ew-r-md);
    background: #fff;
  }
  .ew-fa-item-main { min-width: 0; display: grid; gap: 2px; }
  .ew-fa-link {
    font: var(--ew-fw-medium) var(--ew-fs-body-sm)/1.3 var(--ew-font);
    color: var(--ew-quality); text-decoration: none; word-break: break-all;
  }
  .ew-fa-link:hover { text-decoration: underline; }
  .ew-fa-meta { font: var(--ew-fw-regular) 11px/1.4 var(--ew-font); color: var(--ew-n-400); }
  .ew-fa-remove {
    flex: 0 0 auto; background: transparent; border: 0; cursor: pointer; padding: 2px 6px;
    color: var(--ew-n-400); font-size: 16px; line-height: 1; border-radius: var(--ew-r-sm);
    transition: color var(--ew-dur-fast) var(--ew-ease), background var(--ew-dur-fast) var(--ew-ease);
  }
  .ew-fa-remove:hover { color: var(--ew-danger); background: var(--ew-n-50); }
  .ew-fa-empty { font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); color: var(--ew-n-400); }
  .ew-fa-spinner {
    display: inline-block; width: 11px; height: 11px; border: 2px solid var(--ew-quality);
    border-right-color: transparent; border-radius: 50%; animation: ew-fa-spin 0.7s linear infinite;
    vertical-align: -1px; margin-right: 6px;
  }
  @keyframes ew-fa-spin { to { transform: rotate(360deg); } }
`;

// Pull a file extension (lower-cased) from a name; defaults to 'bin'.
function extOf(name: string): string {
  const i = name.lastIndexOf('.');
  return i >= 0 && i < name.length - 1 ? name.slice(i + 1).toLowerCase() : 'bin';
}

// Human-friendly byte size.
function fmtSize(bytes: number | null): string | null {
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FileAttachments({
  canEdit,
  entityType,
  entityId,
  jobFolderUrl,
  subfolder,
  jobNumber,
  docNumber,
  shortDescSource,
  status,
  round,
  uploadLabel = '＋ Upload to Job Folder',
  bare = false,
}: FileAttachmentsProps) {
  const { instance, accounts } = useMsal();
  const qc = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const attachmentsKey = ['attachments', entityType, entityId];
  const listQuery = useQuery({
    queryKey: attachmentsKey,
    queryFn: () => attachmentsApi.list(entityType, entityId),
    enabled: !!jobFolderUrl,
  });

  // Acquire a Graph token on demand. Silent first; fall back to a popup when
  // interaction is required. Anything else → GraphConsentError (admin consent
  // for the delegated scope is likely still pending).
  const getToken = async (): Promise<string> => {
    try {
      const r = await instance.acquireTokenSilent({ scopes: GRAPH_SCOPES, account: accounts[0] });
      return r.accessToken;
    } catch (e) {
      if (e instanceof InteractionRequiredAuthError) {
        const r = await instance.acquireTokenPopup({ scopes: GRAPH_SCOPES });
        return r.accessToken;
      }
      throw new GraphConsentError('Graph consent required');
    }
  };

  const removeMutation = useMutation({
    mutationFn: (id: number) => attachmentsApi.remove(id),
    // The already-gone 404 never reaches here as an error — attachmentsApi.remove
    // resolves it, so this mutation does not enter isError and the banner below
    // stays quiet. onSettled (not onSuccess) so that any OTHER failure still
    // reconciles the list rather than leaving it stale.
    onSettled: () => qc.invalidateQueries({ queryKey: attachmentsKey }),
  });

  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again re-fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file || !jobFolderUrl) return;

    setUploadError(null);
    setUploading(true);
    try {
      const fileName = buildAttachmentName({
        jobNumber,
        docNumber,
        shortDescSource,
        status,
        ext: extOf(file.name),
      });
      const res = await uploadToJobFolder({ getToken, jobFolderUrl, subfolder, fileName, file });
      await attachmentsApi.create({
        entityType,
        entityId,
        fileName: res.name,
        webUrl: res.webUrl,
        sizeBytes: res.size,
        ...(round ? { roundId: round.id, kind: round.kind } : {}),
      });
      qc.invalidateQueries({ queryKey: attachmentsKey });
    } catch (err) {
      if (err instanceof GraphConsentError) {
        setUploadError('SharePoint access is still being set up (admin consent pending). Try again once it’s granted.');
      } else {
        setUploadError(err instanceof Error ? err.message : 'Upload failed.');
      }
    } finally {
      setUploading(false);
    }
  };

  // ── Not linked: dotted "link your Job Folder first" state ──────────────────
  if (!jobFolderUrl) {
    if (bare) return null;
    return (
      <div className="pw-card ew-fa-card">
        <style>{css}</style>
        <h2 className="pw-card-hd">Job Folder</h2>
        <div className="ew-fa-body">
          <div className="ew-fa-unlinked">
            <div className="ew-fa-unlinked-title">
              <span className="ew-fa-folder-icon" aria-hidden="true">🗀</span>
              Job Folder not linked
            </div>
            <p className="ew-fa-help">
              Link this project’s Job Folder (Projects → edit) before uploading.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Linked: solid card with upload + attachment list ───────────────────────
  // The list query is the whole entity's; a round's side is picked out of it here, so
  // the two sides of a round (and every round) share one request and one cache entry.
  const all = listQuery.data ?? [];
  const files = round ? all.filter((f) => f.roundId === round.id && f.kind === round.kind) : all;

  const body = (
      <div className={bare ? 'ew-fa-body ew-fa-bare' : 'ew-fa-body'}>
        {/*
          Framed as RECORD-KEEPING, not as a step in the send flow. Outgoing mail
          is a mailto: draft, which cannot carry attachments, and we deliberately
          do NOT put Job Folder links in mail to outside reviewers — company
          security blocks external access, so a link would fail silently on their
          end. The PM therefore attaches the real files in Outlook; uploading here
          is what keeps the package on the record. Said plainly so it doesn't read
          as duplicated effort.
        */}
        {canEdit && !bare && (
        <p className="ew-fa-help">
          Files upload to the project's Job Folder (▸ {subfolder}) and stay linked
          here as the record of what went out on this {entityType === 'rfi' ? 'RFI' : 'submittal'}.
          Emails to outside reviewers still need the files attached in Outlook —
          Job Folder links won't open for anyone outside the company.
        </p>
        )}

        {/* Upload control — hidden input + button. Gated: the SharePoint write
            happens client-side before the tracker call, so an ungated button
            leaves an orphaned file in the Job Folder when the API 403s. */}
        {canEdit && (
        <div>
          <input
            ref={fileInputRef}
            type="file"
            style={{ display: 'none' }}
            onChange={onPickFile}
          />
          <Button
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? <><span className="ew-fa-spinner" aria-hidden="true" />Uploading…</> : uploadLabel}
          </Button>
        </div>
        )}

        {uploadError && <div className="ew-fa-error">{uploadError}</div>}

        {/* Attachment list */}
        {listQuery.isError ? (
          <div className="ew-fa-error">{(listQuery.error as Error).message}</div>
        ) : files.length === 0 ? (
          <p className="ew-fa-empty">No files yet.</p>
        ) : (
          <ul className="ew-fa-list">
            {files.map((f) => {
              const who = f.uploadedBy?.displayName ?? f.uploadedBy?.email ?? 'Unknown';
              const size = fmtSize(f.sizeBytes);
              return (
                <li key={f.id} className="ew-fa-item">
                  <span className="ew-fa-item-main">
                    <a className="ew-fa-link" href={f.webUrl} target="_blank" rel="noreferrer">
                      {f.fileName} ↗
                    </a>
                    <span className="ew-fa-meta">
                      {who} · {fmtDateTime(f.uploadedAt)}{size ? ` · ${size}` : ''}
                    </span>
                  </span>
                  {canEdit && (
                  <button
                    type="button"
                    className="ew-fa-remove"
                    title="Remove the link here (the file stays in SharePoint)"
                    disabled={removeMutation.isPending}
                    onClick={() => {
                      if (window.confirm('Remove this file’s link from the tracker? The file stays in SharePoint.')) {
                        removeMutation.mutate(f.id);
                      }
                    }}
                  >
                    ×
                  </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        {removeMutation.isError && (
          <p className="ew-fa-help" style={{ color: 'var(--ew-danger)' }}>
            {(removeMutation.error as Error).message}
          </p>
        )}

        {!bare && (
          <>
            <p className="ew-fa-help">Removes the link here; the file stays in SharePoint.</p>

            <div>
              <p className="ew-fa-label">Naming convention</p>
              <span className="ew-fa-mono">{ATTACHMENT_NAME_HINT}</span>
            </div>
          </>
        )}
      </div>
  );

  // Inside another card (the review rounds): just the list and the upload.
  if (bare) {
    return (
      <>
        <style>{css}</style>
        {body}
      </>
    );
  }

  return (
    <div className="pw-card ew-fa-card">
      <style>{css}</style>
      <h2 className="pw-card-hd">Job Folder</h2>
      {body}
    </div>
  );
}
